import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useCache } from '../contexts/DataCacheContext';
import { getProgramDetails, createWorkout, updateWorkout, deleteWorkout, createExercise, updateExercise, deleteExercise, archiveExercise, getExerciseTemplates, getProgramVolumeDetailed, updateExerciseSupersetGroup, removeSupersetGroup, upsertWorkoutSupersetRest, deleteWorkoutSuperset, updateProgram, exerciseHasLogs } from '../lib/database';
import VolumeBreakdownSheet from './VolumeBreakdownSheet';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSwipeBack } from '../hooks/useSwipeBack';
import { useExerciseAliasIds } from '../hooks/useExerciseAliasIds';
import { filterExerciseTemplates } from '../lib/exerciseSearch';
import ExerciseCatalogViewer from './ExerciseCatalogViewer';
import ProposeExerciseSheet from './ProposeExerciseSheet';
import './ProgramEditor.css';

function ProgramEditor() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();

  // L'editor muta la scheda con molti handler diversi: all'uscita butta le
  // cache di dashboard (scheda attiva/prossimo allenamento, TTL 60s) e lista
  // schede (TTL 120s), così le modifiche sono subito visibili. Invalidare
  // sempre è a costo di un refetch: accettabile e più robusto di un dirty-flag.
  const profileIdRef = useRef(null);
  profileIdRef.current = profile?.id;
  useEffect(() => {
    return () => {
      if (profileIdRef.current) {
        invalidateCache(`athlete-dashboard-${profileIdRef.current}`);
        invalidateCache(`athlete-programs-${profileIdRef.current}`);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [program, setProgram] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  const [programVolume, setProgramVolume] = useState({});
  const [isVolumeExpanded, setIsVolumeExpanded] = useState(false);
  const [selectedMuscle, setSelectedMuscle] = useState(null); // drill-down volume per gruppo

  const [supersetMode, setSupersetMode] = useState(null);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [showCatalogViewer, setShowCatalogViewer] = useState(false);

  // Program rename state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const nameInputRef = useRef(null);

  // Workout edit state
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [showEditWorkoutModal, setShowEditWorkoutModal] = useState(false);

  useEffect(() => {
    if (programId && profile?.id) {
      loadProgramData();
    }
  }, [programId, profile]);

  useEffect(() => {
    if (programId) {
      loadProgramVolume();
    }
  }, [programId, workouts]);

  const loadProgramVolume = async () => {
    try {
      const volume = await getProgramVolumeDetailed(programId);
      setProgramVolume(volume);
    } catch (error) {
      console.error('Error loading program volume:', error);
      setProgramVolume({});
    }
  };

  const loadProgramData = async (showLoading = true) => {
    try {
      // Only show loading on initial load, not on refreshes
      if (showLoading && !program) {
        setLoading(true);
      }
      const data = await getProgramDetails(programId);

      if (profile.role === 'athlete' && data.creator_id !== profile.id) {
        toast.error('Non hai i permessi per modificare questa scheda');
        navigate('/athlete/programs');
        return;
      }

      setProgram(data);
      setWorkouts(data.workouts || []);
    } catch (error) {
      console.error('Error loading program:', error);
      setProgram(null);
      setWorkouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (profile.role === 'athlete') {
      navigate('/athlete/programs');
    } else {
      navigate('/coach');
    }
  };

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(handleBack);

  // Program rename handlers
  const handleStartRename = () => {
    setEditedName(program?.name || '');
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleSaveRename = async () => {
    const trimmedName = editedName.trim();
    if (!trimmedName || trimmedName === program?.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateProgram(programId, { name: trimmedName });
      setProgram(prev => ({ ...prev, name: trimmedName }));
      setIsEditingName(false);
    } catch (error) {
      console.error('Error renaming program:', error);
      toast.error('Errore nella rinomina della scheda');
    }
  };

  const handleCancelRename = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  // Workout edit handlers
  const handleOpenEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setShowEditWorkoutModal(true);
  };

  const handleUpdateWorkoutSubmit = async (workoutData) => {
    try {
      // Close modal immediately
      setShowEditWorkoutModal(false);
      const workoutId = editingWorkout.id;
      setEditingWorkout(null);

      // Update workout
      await updateWorkout(workoutId, workoutData);

      // Update local state
      setWorkouts(prev => prev.map(w =>
        w.id === workoutId ? { ...w, ...workoutData } : w
      ));

      toast.success('Workout aggiornato');
    } catch (error) {
      console.error('Error updating workout:', error);
      toast.error('Errore nell\'aggiornamento del workout');
    }
  };

  const handleAddWorkout = () => setShowWorkoutModal(true);

  const handleCreateWorkoutSubmit = async (workoutData) => {
    try {
      // Close modal immediately
      setShowWorkoutModal(false);

      await createWorkout(programId, workoutData);
      await loadProgramData(false);
    } catch (error) {
      console.error('Error creating workout:', error);
      toast.error('Errore nella creazione del workout');
      await loadProgramData(false);
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo workout?')) return;
    try {
      // Optimistic: remove locally
      setWorkouts(prev => prev.filter(w => w.id !== workoutId));
      await deleteWorkout(workoutId);
      await loadProgramData(false);
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error('Errore nell\'eliminazione del workout');
      await loadProgramData(false);
    }
  };

  const handleAddExercise = (workout) => {
    setSelectedWorkout(workout);
    setShowExerciseModal(true);
  };

  const handleCreateExerciseSubmit = async (exerciseData) => {
    try {
      const maxOrder = selectedWorkout.exercises?.length || 0;
      const newExerciseData = { ...exerciseData, exercise_order: maxOrder + 1 };

      // Optimistic UI: add exercise locally immediately
      const tempId = `temp-${Date.now()}`;
      const optimisticExercise = { id: tempId, ...newExerciseData };
      setWorkouts(prev => prev.map(w =>
        w.id === selectedWorkout.id
          ? { ...w, exercises: [...(w.exercises || []), optimisticExercise] }
          : w
      ));

      // Close modal immediately for snappy UX
      setShowExerciseModal(false);
      setSelectedWorkout(null);

      // Save to database in background
      await createExercise(selectedWorkout.id, newExerciseData);

      // Refresh data silently to get real ID
      await loadProgramData(false);
    } catch (error) {
      console.error('Error creating exercise:', error);
      toast.error('Errore nella creazione dell\'esercizio');
      // Rollback: reload from server
      await loadProgramData(false);
    }
  };

  const handleEditExercise = (exercise, workout) => {
    setSelectedWorkout(workout);
    setEditingExercise(exercise);
    setShowExerciseModal(true);
  };

  const handleUpdateExerciseSubmit = async (exerciseData) => {
    try {
      const exerciseId = editingExercise.id;

      // Optimistic: update locally
      setWorkouts(prev => prev.map(w => ({
        ...w,
        exercises: w.exercises?.map(ex =>
          ex.id === exerciseId ? { ...ex, ...exerciseData } : ex
        )
      })));

      // Close modal immediately
      setShowExerciseModal(false);
      setEditingExercise(null);
      setSelectedWorkout(null);

      await updateExercise(exerciseId, exerciseData);
      await loadProgramData(false);
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast.error('Errore nell\'aggiornamento dell\'esercizio');
      await loadProgramData(false);
    }
  };

  const handleDeleteExercise = async (exerciseId, exerciseName = 'questo esercizio') => {
    try {
      // Se l'esercizio ha già storico, NON si può cancellare la riga: la FK
      // exercise_logs.exercise_id è ON DELETE CASCADE → cancellerebbe lo storico.
      // In quel caso si ARCHIVIA (rimozione dalla sola programmazione futura).
      const hasLogs = await exerciseHasLogs(exerciseId);

      if (hasLogs) {
        if (!window.confirm(
          `Rimuovere "${exerciseName}" dalla scheda? Verrà rimosso solo dai prossimi ` +
          `allenamenti; lo storico già registrato resterà invariato.`
        )) return;
      } else {
        if (!window.confirm('Sei sicuro di voler eliminare questo esercizio?')) return;
      }

      // PB-011: se l'esercizio e' in una superserie e la sua rimozione lascia il
      // gruppo con < 2 membri, il record workout_supersets va eliminato (evita
      // orfani ed eredita' sul riuso della lettera). I rest_seconds individuali
      // restano invariati.
      let ssCleanup = null;
      for (const w of workouts) {
        const ex = w.exercises?.find(e => e.id === exerciseId);
        if (ex) {
          if (ex.superset_group) {
            const remaining = w.exercises.filter(e => e.id !== exerciseId && e.superset_group === ex.superset_group).length;
            if (remaining < 2) ssCleanup = { workoutId: w.id, letter: ex.superset_group };
          }
          break;
        }
      }

      // Optimistic: remove locally
      setWorkouts(prev => prev.map(w => ({
        ...w,
        exercises: w.exercises?.filter(ex => ex.id !== exerciseId)
      })));

      // Con storico → soft-delete (archivia); senza storico → delete pulito.
      if (hasLogs) {
        await archiveExercise(exerciseId);
      } else {
        await deleteExercise(exerciseId);
      }
      if (ssCleanup) {
        try { await deleteWorkoutSuperset(ssCleanup.workoutId, ssCleanup.letter); }
        catch (e) { console.error('Superset record cleanup failed:', e); }
      }
      await loadProgramData(false);
      toast.success(hasLogs ? 'Esercizio rimosso dalla scheda' : 'Esercizio eliminato');
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Errore nella rimozione dell\'esercizio');
      await loadProgramData(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = async (event, workoutId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const workout = workouts.find(w => w.id === workoutId);
    if (!workout || !workout.exercises) return;
    const oldIndex = workout.exercises.findIndex(ex => ex.id === active.id);
    const newIndex = workout.exercises.findIndex(ex => ex.id === over.id);
    const reordered = arrayMove(workout.exercises, oldIndex, newIndex);
    // Optimistic update: update local state immediately
    const updatedReordered = reordered.map((ex, i) => ({ ...ex, exercise_order: i + 1 }));
    setWorkouts(prev => prev.map(w =>
      w.id === workoutId ? { ...w, exercises: updatedReordered } : w
    ));
    try {
      await Promise.all(updatedReordered.map((ex) => updateExercise(ex.id, { exercise_order: ex.exercise_order })));
    } catch (error) {
      console.error('Error reordering exercises:', error);
      await loadProgramData(false);
    }
  };

  const handleMoveExercise = async (workoutId, exerciseId, direction) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (!workout || !workout.exercises) return;
    const sorted = [...workout.exercises].sort((a, b) => (a.exercise_order || 0) - (b.exercise_order || 0));
    const idx = sorted.findIndex(ex => ex.id === exerciseId);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sorted.length - 1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    const reordered = arrayMove(sorted, idx, newIdx);
    // Optimistic update: update local state immediately
    const updatedReordered = reordered.map((ex, i) => ({ ...ex, exercise_order: i + 1 }));
    setWorkouts(prev => prev.map(w =>
      w.id === workoutId ? { ...w, exercises: updatedReordered } : w
    ));
    try {
      await Promise.all(updatedReordered.map((ex) => updateExercise(ex.id, { exercise_order: ex.exercise_order })));
    } catch (error) {
      console.error('Error reordering exercises:', error);
      await loadProgramData(false);
    }
  };

  const getNextSupersetLetter = (workout) => {
    if (!workout.exercises || workout.exercises.length === 0) return 'A';
    const existing = new Set(workout.exercises.map(ex => ex.superset_group).filter(Boolean));
    for (let letter of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
      if (!existing.has(letter)) return letter;
    }
    return 'Z';
  };

  const handleEnterSupersetMode = (workoutId) => {
    setSupersetMode(workoutId);
    setSelectedExercises([]);
  };

  const handleCancelSupersetMode = () => {
    setSupersetMode(null);
    setSelectedExercises([]);
  };

  const handleToggleExerciseSelection = (exerciseId) => {
    setSelectedExercises(prev =>
      prev.includes(exerciseId) ? prev.filter(id => id !== exerciseId) : [...prev, exerciseId]
    );
  };

  const handleCreateSuperset = async (workout) => {
    if (selectedExercises.length < 2) {
      toast.warning('Seleziona almeno 2 esercizi per creare una superserie');
      return;
    }
    const sorted = [...workout.exercises].sort((a, b) => (a.exercise_order || 0) - (b.exercise_order || 0));
    const ids = sorted.map(ex => ex.id);
    const indices = selectedExercises.map(id => ids.indexOf(id)).sort((a, b) => a - b);
    for (let i = 0; i < indices.length - 1; i++) {
      if (indices[i + 1] !== indices[i] + 1) {
        toast.warning('Gli esercizi selezionati devono essere consecutivi nella lista');
        return;
      }
    }
    try {
      const letter = getNextSupersetLetter(workout);

      // Optimistic: update locally
      setWorkouts(prev => prev.map(w => ({
        ...w,
        exercises: w.exercises?.map(ex =>
          selectedExercises.includes(ex.id) ? { ...ex, superset_group: letter } : ex
        )
      })));

      setSupersetMode(null);
      setSelectedExercises([]);

      await updateExerciseSupersetGroup(selectedExercises, letter);
      await loadProgramData(false);
    } catch (error) {
      console.error('Error creating superset:', error);
      toast.error('Errore nella creazione della superserie');
      await loadProgramData(false);
    }
  };

  const handleRemoveSuperset = async (supersetGroup, workoutId) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (!workout) return;
    const exerciseIds = workout.exercises.filter(ex => ex.superset_group === supersetGroup).map(ex => ex.id);
    if (!window.confirm(`Rimuovere superserie ${supersetGroup}?`)) return;
    try {
      // Optimistic: remove locally
      setWorkouts(prev => prev.map(w => ({
        ...w,
        exercises: w.exercises?.map(ex =>
          exerciseIds.includes(ex.id) ? { ...ex, superset_group: null } : ex
        )
      })));

      await removeSupersetGroup(exerciseIds);
      // PB-011 (J6): allo scioglimento elimina il record del gruppo, cosi' un
      // futuro riuso della stessa lettera non eredita il recupero precedente.
      // I rest_seconds individuali NON vengono toccati.
      await deleteWorkoutSuperset(workoutId, supersetGroup);
      await loadProgramData(false);
    } catch (error) {
      console.error('Error removing superset:', error);
      await loadProgramData(false);
    }
  };

  // PB-011: il "Recupero tra i giri" e' una proprieta' della superserie (workout
  // + lettera). Vuoto -> null ("non impostato"), 0 -> nessun recupero esplicito.
  // Non tocca mai i rest_seconds individuali degli esercizi.
  const handleSetSupersetRest = async (workoutId, letter, value) => {
    setWorkouts(prev => prev.map(w =>
      w.id === workoutId
        ? { ...w, supersetRestByLetter: { ...(w.supersetRestByLetter || {}), [letter]: value } }
        : w
    ));
    try {
      await upsertWorkoutSupersetRest(workoutId, letter, value);
    } catch (error) {
      console.error('Error saving superset rest:', error);
      toast.error('Errore nel salvataggio del recupero della superserie');
      await loadProgramData(false);
    }
  };

  const getSupersetColor = (group) => {
    const colors = {
      'A': '#3b82f6', 'B': '#10b981', 'C': '#a855f7', 'D': '#ec4899', 'E': '#f59e0b',
    };
    return colors[group] || colors['A'];
  };

  const volumeMuscles = [
    'Petto', 'Schiena', 'Spalle', 'Bicipiti', 'Tricipiti', 'Avambracci',
    'Quadricipiti', 'Femorali', 'Glutei', 'Adduttori', 'Polpacci', 'Core'
  ];
  // Serie totali reali del programma (ogni serie contata una volta, dai workout)
  const totalSets = workouts.reduce(
    (sum, w) => sum + (w.exercises || []).reduce((a, ex) => a + (Number(ex.sets) || 0), 0),
    0
  );
  const displayTotalSets = totalSets % 1 === 0 ? totalSets : totalSets.toFixed(1);

  if (loading) {
    return <div className="pe-container"><div className="pe-loading">Caricamento...</div></div>;
  }

  if (!program) {
    return (
      <div className="pe-container">
        <div className="pe-loading">
          <p>Programma non trovato</p>
          <button onClick={handleBack} className="pe-btn-back-home">Torna indietro</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pe-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Sticky Header */}
      <header className="pe-header">
        <button className="pe-header-back" onClick={handleBack} aria-label="Indietro">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="pe-header-text">
          <span className="pe-header-label">Modifica scheda</span>
          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveRename();
                } else if (e.key === 'Escape') {
                  handleCancelRename();
                }
              }}
              className="pe-header-title-input"
            />
          ) : (
            <span className="pe-header-title pe-header-title--editable" onClick={handleStartRename}>
              {program.name}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pe-edit-icon">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </span>
          )}
        </div>
        <button className="pe-header-catalog" onClick={() => setShowCatalogViewer(true)}>
          Catalogo
        </button>
      </header>

      <main className="pe-main">
        {/* Volume Accordion */}
        <div className={`pe-volume ${isVolumeExpanded ? 'pe-volume--open' : ''}`}>
          <button className="pe-volume-header" onClick={() => setIsVolumeExpanded(!isVolumeExpanded)}>
            <span className="pe-volume-title">Volume Totale</span>
            <span className="pe-volume-summary">{displayTotalSets} serie</span>
            <span className={`pe-volume-chevron ${isVolumeExpanded ? 'pe-volume-chevron--open' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
          {isVolumeExpanded && (
            <div className="pe-volume-grid">
              {volumeMuscles.map(muscle => {
                const gd = programVolume[muscle];
                const sets = gd?.total || 0;
                if (!sets) return null;
                const d = sets % 1 === 0 ? sets : sets.toFixed(1);
                return (
                  <button
                    key={muscle}
                    type="button"
                    className="pe-volume-row"
                    onClick={() => setSelectedMuscle(muscle)}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <span className="pe-volume-muscle">{muscle}</span>
                    <span className="pe-volume-sets">{d} &rsaquo;</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Workouts */}
        {workouts.length === 0 ? (
          <div className="pe-empty">
            <p className="pe-empty-title">Nessun workout</p>
            <p className="pe-empty-sub">Aggiungi il primo allenamento</p>
          </div>
        ) : (
          <div className="pe-workouts">
            {workouts.map(workout => (
              <div key={workout.id} className="pe-workout">
                <div className="pe-workout-header">
                  <div className="pe-workout-info">
                    <span className="pe-workout-day">G{workout.day_number}</span>
                    <div>
                      <span
                        className="pe-workout-name pe-workout-name--editable"
                        onClick={() => handleOpenEditWorkout(workout)}
                        title="Clicca per modificare"
                      >
                        {workout.name}
                      </span>
                      {workout.description && <span className="pe-workout-desc">{workout.description}</span>}
                    </div>
                  </div>
                  <button className="pe-workout-delete" onClick={() => handleDeleteWorkout(workout.id)} aria-label="Elimina workout">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                <div className="pe-exercises">
                  {workout.exercises && workout.exercises.length > 0 ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, workout.id)}>
                      <SortableContext items={workout.exercises.map(ex => ex.id)} strategy={verticalListSortingStrategy}>
                        {(() => {
                          // PB-012: contenitore REALE della superserie (wrapper) che
                          // racchiude header + esercizi membri. NESSUNA modifica alla
                          // logica DnD: `SortableContext` e `items` invariati, ogni
                          // SortableExercise resta un nodo sortable con lo STESSO id
                          // (`useSortable({ id: exercise.id })`); il wrapper è solo
                          // presentazionale. L'ordine dei blocchi rispecchia esattamente
                          // l'array ordinato per `exercise_order`, quindi coincide con
                          // l'ordine degli id in `items`.
                          const sorted = [...workout.exercises].sort((a, b) => (a.exercise_order || 0) - (b.exercise_order || 0));
                          const inSelectMode = supersetMode === workout.id;
                          const total = sorted.length;
                          const renderCard = (ex, gi, inGroup) => (
                            <SortableExercise
                              key={ex.id}
                              exercise={ex}
                              isFirst={gi === 0}
                              isLast={gi === total - 1}
                              onEdit={(e) => handleEditExercise(e, workout)}
                              onDelete={handleDeleteExercise}
                              onMoveUp={() => handleMoveExercise(workout.id, ex.id, 'up')}
                              onMoveDown={() => handleMoveExercise(workout.id, ex.id, 'down')}
                              supersetMode={inSelectMode}
                              isSelected={selectedExercises.includes(ex.id)}
                              onToggleSelection={() => handleToggleExerciseSelection(ex.id)}
                              getSupersetColor={getSupersetColor}
                              onRemoveSuperset={(group) => handleRemoveSuperset(group, workout.id)}
                              inSupersetGroup={inGroup}
                            />
                          );
                          const blocks = [];
                          let i = 0;
                          while (i < total) {
                            const ex = sorted[i];
                            const letter = ex.superset_group;
                            // In modalità selezione superserie si rende piatto (come prima),
                            // per non interferire con le checkbox di selezione.
                            if (letter && !inSelectMode) {
                              let j = i;
                              while (j < total && sorted[j].superset_group === letter) j++;
                              const members = sorted.slice(i, j);
                              if (members.length >= 2) {
                                const color = getSupersetColor(letter);
                                blocks.push(
                                  <div key={`ssg-${letter}-${members[0].id}`} className="pe-ss-group" style={{ borderColor: color }}>
                                    <SupersetHeader
                                      letter={letter}
                                      color={color}
                                      rest={workout.supersetRestByLetter?.[letter]}
                                      onCommit={(val) => handleSetSupersetRest(workout.id, letter, val)}
                                      onDissolve={() => handleRemoveSuperset(letter, workout.id)}
                                    />
                                    <div className="pe-ss-group-body">
                                      {members.map((m, mi) => renderCard(m, i + mi, true))}
                                    </div>
                                  </div>
                                );
                                i = j;
                                continue;
                              }
                            }
                            blocks.push(renderCard(ex, i, false));
                            i++;
                          }
                          return blocks;
                        })()}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <p className="pe-no-exercises">Nessun esercizio</p>
                  )}

                  {supersetMode === workout.id ? (
                    <div className="pe-superset-bar">
                      <span className="pe-superset-hint">Seleziona 2+ esercizi consecutivi</span>
                      <div className="pe-superset-actions">
                        <button
                          className="pe-btn-ss-confirm"
                          onClick={() => handleCreateSuperset(workout)}
                          disabled={selectedExercises.length < 2}
                        >
                          Conferma ({selectedExercises.length})
                        </button>
                        <button className="pe-btn-ss-cancel" onClick={handleCancelSupersetMode}>Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <div className="pe-workout-actions">
                      <button className="pe-btn-add-exercise" onClick={() => handleAddExercise(workout)}>
                        + Aggiungi Esercizio
                      </button>
                      {workout.exercises && workout.exercises.length >= 2 && (
                        <button className="pe-btn-superset" onClick={() => handleEnterSupersetMode(workout.id)}>
                          Crea Superserie
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <button className="pe-btn-add-workout" onClick={handleAddWorkout}>
          + Aggiungi Workout
        </button>
      </main>

      <CreateWorkoutModal isOpen={showWorkoutModal} onClose={() => setShowWorkoutModal(false)} onCreate={handleCreateWorkoutSubmit} />
      <EditWorkoutModal
        isOpen={showEditWorkoutModal}
        onClose={() => { setShowEditWorkoutModal(false); setEditingWorkout(null); }}
        onUpdate={handleUpdateWorkoutSubmit}
        workout={editingWorkout}
      />
      <AddExerciseModal
        isOpen={showExerciseModal}
        onClose={() => { setShowExerciseModal(false); setSelectedWorkout(null); setEditingExercise(null); }}
        onCreate={handleCreateExerciseSubmit}
        onUpdate={handleUpdateExerciseSubmit}
        workoutName={selectedWorkout?.name}
        editingExercise={editingExercise}
      />
      <ExerciseCatalogViewer isOpen={showCatalogViewer} onClose={() => setShowCatalogViewer(false)} />
      {selectedMuscle && (
        <VolumeBreakdownSheet
          muscleGroup={selectedMuscle}
          volumeData={programVolume}
          onClose={() => setSelectedMuscle(null)}
        />
      )}
    </div>
  );
}

/* ===== CreateWorkoutModal ===== */
function CreateWorkoutModal({ isOpen, onClose, onCreate }) {
  useScrollLock(isOpen);
  const [name, setName] = useState('');
  const [dayNumber, setDayNumber] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && dayNumber) {
      onCreate({ name: name.trim(), day_number: parseInt(dayNumber), description: description.trim() });
      setName(''); setDayNumber(''); setDescription('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pe-modal-overlay" onClick={onClose}>
      <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pe-modal-header">
          <h2 className="pe-modal-title">Nuovo Workout</h2>
          <button className="pe-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="pe-modal-form">
          <div className="pe-form-row-2">
            <div className="pe-field">
              <label>Nome</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Petto/Tricipiti" required />
            </div>
            <div className="pe-field pe-field--small">
              <label>Giorno</label>
              <input type="number" value={dayNumber} onChange={(e) => setDayNumber(e.target.value)} placeholder="1" min="1" required inputMode="numeric" />
            </div>
          </div>
          <div className="pe-field">
            <label>Descrizione <span className="pe-optional">(opz.)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrizione..." rows="2" />
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Non inserire informazioni mediche, diagnosi o dati sanitari.</p>
          </div>
          <div className="pe-modal-actions">
            <button type="button" className="pe-btn-cancel" onClick={onClose}>Annulla</button>
            <button type="submit" className="pe-btn-submit">Crea</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== EditWorkoutModal ===== */
function EditWorkoutModal({ isOpen, onClose, onUpdate, workout }) {
  useScrollLock(isOpen);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Initialize form when workout changes
  useEffect(() => {
    if (workout) {
      setName(workout.name || '');
      setDescription(workout.description || '');
    }
  }, [workout]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onUpdate({ name: name.trim(), description: description.trim() });
    }
  };

  if (!isOpen || !workout) return null;

  return (
    <div className="pe-modal-overlay" onClick={onClose}>
      <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pe-modal-header">
          <h2 className="pe-modal-title">Modifica Workout</h2>
          <button className="pe-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="pe-modal-form">
          <div className="pe-field">
            <label>Nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Petto/Tricipiti" required autoFocus />
          </div>
          <div className="pe-field">
            <label>Descrizione <span className="pe-optional">(opz.)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="es. Focus sul volume..." rows="3" />
          </div>
          <div className="pe-modal-actions">
            <button type="button" className="pe-btn-cancel" onClick={onClose}>Annulla</button>
            <button type="submit" className="pe-btn-submit">Salva</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== AddExerciseModal ===== */
function AddExerciseModal({ isOpen, onClose, onCreate, onUpdate, workoutName, editingExercise }) {
  useScrollLock(isOpen);
  const { profile } = useAuth();
  const [name, setName] = useState('');
  const [sets, setSets] = useState('');
  const [reps, setReps] = useState('');
  const [weight, setWeight] = useState('');
  const [restSeconds, setRestSeconds] = useState('');
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCustomExercise, setIsCustomExercise] = useState(false);
  const [showProposeSheet, setShowProposeSheet] = useState(false);

  // Cardio-specific fields
  const [durationMinutes, setDurationMinutes] = useState('');
  const [targetSpeed, setTargetSpeed] = useState('');
  const [targetIncline, setTargetIncline] = useState('');
  const [targetDistance, setTargetDistance] = useState('');

  const isEditMode = !!editingExercise;

  // Detect if current exercise is cardio
  const isCardio = selectedTemplate?.primaryMuscle === 'Cardio' ||
                   (isEditMode && editingExercise?.duration_minutes != null);

  // PB-011: se l'esercizio appartiene a una superserie, il recupero e' gestito
  // dalla superserie → nascondiamo il campo individuale (senza toccarne il valore).
  const inSuperset = isEditMode && !!editingExercise?.superset_group;

  // Interactive swipe down to close
  const [modalDragY, setModalDragY] = useState(0);
  const [isModalDragging, setIsModalDragging] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const modalContentRef = useRef(null);
  const modalDragStartY = useRef(0);
  const modalDragSource = useRef(null);
  const modalCloseTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(modalCloseTimerRef.current), []);

  const handleModalClose = useCallback(() => {
    setIsModalClosing(true);
    clearTimeout(modalCloseTimerRef.current);
    modalCloseTimerRef.current = setTimeout(() => {
      setIsModalClosing(false);
      setModalDragY(0);
      onClose();
    }, 250);
  }, [onClose]);

  const handleModalTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const modal = modalContentRef.current;
    const modalRect = modal?.getBoundingClientRect();
    const touchRelativeY = touch.clientY - (modalRect?.top || 0);
    const isInHandleArea = touchRelativeY < 60;
    const isScrolledToTop = !modal || modal.scrollTop <= 0;

    if (isInHandleArea) {
      modalDragSource.current = 'handle';
      modalDragStartY.current = touch.clientY;
      setIsModalDragging(true);
      return;
    }
    if (isScrolledToTop) {
      modalDragSource.current = 'content';
      modalDragStartY.current = touch.clientY;
    }
  }, []);

  const handleModalTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const delta = touch.clientY - modalDragStartY.current;

    if (!isModalDragging && modalDragSource.current === 'content') {
      if (delta > 8) {
        setIsModalDragging(true);
        modalDragStartY.current = touch.clientY;
        return;
      } else if (delta < -5) {
        modalDragSource.current = null;
        return;
      }
      return;
    }
    if (!isModalDragging) return;
    if (delta > 0) e.preventDefault();
    setModalDragY(delta > 0 ? delta : delta * 0.2);
  }, [isModalDragging]);

  const handleModalTouchEnd = useCallback(() => {
    if (!isModalDragging) {
      modalDragSource.current = null;
      return;
    }
    setIsModalDragging(false);
    modalDragSource.current = null;
    if (modalDragY > 120) {
      handleModalClose();
    } else {
      setModalDragY(0);
    }
  }, [isModalDragging, modalDragY, handleModalClose]);

  useEffect(() => {
    if (editingExercise) {
      setName(editingExercise.name || '');
      setSets(editingExercise.sets?.toString() || '');
      setReps(editingExercise.reps || '');
      setWeight(editingExercise.weight?.toString() || '');
      setRestSeconds(editingExercise.rest_seconds?.toString() || '');
      setNotes(editingExercise.notes || '');
      setSearchQuery(editingExercise.name || '');
      // Cardio fields
      setDurationMinutes(editingExercise.duration_minutes?.toString() || '');
      setTargetSpeed(editingExercise.target_speed?.toString() || '');
      setTargetIncline(editingExercise.target_incline?.toString() || '');
      setTargetDistance(editingExercise.target_distance?.toString() || '');
    } else {
      setName(''); setSets(''); setReps(''); setWeight(''); setRestSeconds(''); setNotes('');
      setSearchQuery(''); setSelectedTemplate(null); setIsCustomExercise(false);
      // Reset cardio fields
      setDurationMinutes(''); setTargetSpeed(''); setTargetIncline(''); setTargetDistance('');
    }
  }, [editingExercise]);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const data = await getExerciseTemplates();
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (error) {
      setTemplates([]);
      setFilteredTemplates([]);
    }
  };

  // Ricerca alias-aware come nel catalogo: match locale su nome e token,
  // arricchito dagli alias risolti dalla RPC.
  const aliasIds = useExerciseAliasIds(searchQuery);

  useEffect(() => {
    setFilteredTemplates(filterExerciseTemplates(templates, searchQuery, aliasIds));
    setShowDropdown(searchQuery.length > 0 && !selectedTemplate && !isEditMode);
  }, [searchQuery, templates, selectedTemplate, isEditMode, aliasIds]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation based on exercise type
    if (isCardio) {
      if (!name.trim() || !durationMinutes) return;
    } else {
      if (!name.trim() || !sets || !reps.trim()) return;
    }

    const data = {
      name: name.trim(),
      notes: notes.trim() || null,
      exercise_template_id: selectedTemplate?.id || null
    };

    if (isCardio) {
      // Cardio exercise data
      data.sets = 1; // Cardio is typically 1 "set"
      data.reps = `${durationMinutes} min`; // Store duration as reps for display
      data.weight = null;
      data.duration_minutes = parseInt(durationMinutes);
      data.target_speed = targetSpeed ? parseFloat(targetSpeed) : null;
      data.target_incline = targetIncline ? parseFloat(targetIncline) : null;
      data.target_distance = targetDistance ? parseFloat(targetDistance) : null;
    } else {
      // Strength exercise data
      data.sets = parseInt(sets);
      data.reps = reps.trim();
      data.weight = weight ? parseFloat(weight) : null;
      data.rest_seconds = restSeconds ? parseInt(restSeconds) : null;
    }

    if (isEditMode) { onUpdate(data); } else { onCreate(data); }

    if (!isEditMode) {
      setName(''); setSets(''); setReps(''); setWeight(''); setRestSeconds(''); setNotes('');
      setSearchQuery(''); setSelectedTemplate(null); setIsCustomExercise(false);
      setDurationMinutes(''); setTargetSpeed(''); setTargetIncline(''); setTargetDistance('');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="pe-modal-overlay"
      style={{ backgroundColor: `rgba(0,0,0,${Math.max(0, 0.5 - modalDragY / 600)})` }}
      onClick={handleModalClose}
    >
      <div
        ref={modalContentRef}
        className={`pe-modal pe-modal--exercise${isModalClosing ? ' pe-modal--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleModalTouchStart}
        onTouchMove={handleModalTouchMove}
        onTouchEnd={handleModalTouchEnd}
        style={{
          transform: isModalClosing ? 'translateY(100%)' : `translateY(${Math.max(0, modalDragY)}px)`,
          transition: isModalDragging ? 'none' : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
        }}
      >
        <div className="pe-modal-handle" />
        <div className="pe-modal-header">
          <h2 className="pe-modal-title">
            {isEditMode ? 'Modifica' : 'Nuovo Esercizio'}
            {workoutName && <span className="pe-modal-subtitle"> — {workoutName}</span>}
          </h2>
          <button className="pe-modal-close" onClick={handleModalClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="pe-modal-form">
          {/* Name with autocomplete */}
          <div className="pe-field">
            <label>Nome Esercizio</label>
            <div className="pe-autocomplete">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setName(e.target.value); setSelectedTemplate(null); setIsCustomExercise(false); }}
                onFocus={() => !isEditMode && setShowDropdown(true)}
                placeholder="Cerca o scrivi nome..."
                required
              />
              {showDropdown && filteredTemplates.length > 0 && (
                <div className="pe-autocomplete-list">
                  {filteredTemplates.slice(0, 8).map(t => (
                    <div key={t.id} className="pe-autocomplete-item" onClick={() => {
                      setSelectedTemplate(t); setSearchQuery(t.name); setName(t.name); setShowDropdown(false);
                    }}>
                      <span className="pe-ac-name">{t.name}</span>
                      <span className="pe-ac-muscle">{t.primaryMuscle}</span>
                    </div>
                  ))}
                  {searchQuery.length >= 2 && (
                    <div className="pe-autocomplete-item pe-ac-custom" onClick={() => {
                      setIsCustomExercise(true); setSelectedTemplate(null); setName(searchQuery); setShowDropdown(false);
                    }}>
                      <span className="pe-ac-name">+ Custom: "{searchQuery}"</span>
                    </div>
                  )}
                </div>
              )}

              {showDropdown && searchQuery.length >= 2 && filteredTemplates.length === 0 && (
                <button
                  type="button"
                  onClick={() => { setShowDropdown(false); setShowProposeSheet(true); }}
                  className="pe-propose-cta"
                >
                  Non trovi "{searchQuery}"? Proponilo allo staff
                </button>
              )}

              {selectedTemplate && !isEditMode && (
                <div className="pe-ac-badge">{selectedTemplate.primaryMuscle || 'Proposta in attesa'}{selectedTemplate.isPending && ' (pending)'}</div>
              )}
              {isCustomExercise && !isEditMode && (
                <div className="pe-ac-badge pe-ac-badge--custom">Custom</div>
              )}
            </div>
          </div>

          {isCardio ? (
            <>
              {/* Cardio: Durata / Distanza row */}
              <div className="pe-form-row-2">
                <div className="pe-field">
                  <label>Durata <span className="pe-optional">(min)</span></label>
                  <input type="number" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} placeholder="30" min="1" required inputMode="numeric" />
                </div>
                <div className="pe-field">
                  <label>Distanza <span className="pe-optional">(km)</span></label>
                  <input type="number" value={targetDistance} onChange={(e) => setTargetDistance(e.target.value)} placeholder="5" min="0" step="0.1" inputMode="decimal" />
                </div>
              </div>

              {/* Cardio: Velocità / Inclinazione row */}
              <div className="pe-form-row-2">
                <div className="pe-field">
                  <label>Velocità <span className="pe-optional">(km/h)</span></label>
                  <input type="number" value={targetSpeed} onChange={(e) => setTargetSpeed(e.target.value)} placeholder="8" min="0" step="0.5" inputMode="decimal" />
                </div>
                <div className="pe-field">
                  <label>Inclinazione <span className="pe-optional">(%)</span></label>
                  <input type="number" value={targetIncline} onChange={(e) => setTargetIncline(e.target.value)} placeholder="2" min="0" max="30" step="0.5" inputMode="decimal" />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Strength: Serie / Reps row */}
              <div className="pe-form-row-2">
                <div className="pe-field">
                  <label>Serie</label>
                  <input type="number" value={sets} onChange={(e) => setSets(e.target.value)} placeholder="4" min="1" required inputMode="numeric" />
                </div>
                <div className="pe-field">
                  <label>Ripetizioni</label>
                  <input type="text" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="8-10" required inputMode="text" />
                </div>
              </div>

              {/* Strength: Peso / Recupero row */}
              <div className="pe-form-row-2">
                <div className="pe-field">
                  <label>Peso <span className="pe-optional">(kg)</span></label>
                  <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="50" min="0" step="0.5" inputMode="decimal" />
                </div>
                <div className="pe-field">
                  <label>Recupero <span className="pe-optional">(sec)</span></label>
                  {inSuperset ? (
                    <p className="pe-field-ss-hint">Il recupero è gestito dalla superserie</p>
                  ) : (
                    <input type="number" value={restSeconds} onChange={(e) => setRestSeconds(e.target.value)} placeholder="60" min="0" inputMode="numeric" />
                  )}
                </div>
              </div>
            </>
          )}

          {/* Note */}
          <div className="pe-field">
            <label>Note <span className="pe-optional">(opz.)</span></label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="es. Focus su discesa lenta" rows="2" />
          </div>

          <div className="pe-modal-actions">
            <button type="button" className="pe-btn-cancel" onClick={handleModalClose}>Annulla</button>
            <button type="submit" className="pe-btn-submit">
              {isEditMode ? 'Salva' : 'Aggiungi'}
            </button>
          </div>
        </form>
      </div>

      {showProposeSheet && (
        <ProposeExerciseSheet
          initialName={searchQuery}
          userId={profile?.id}
          userRole={profile?.role}
          allowAddToSession={true}
          onClose={() => setShowProposeSheet(false)}
          onProposed={({ template }) => {
            setShowProposeSheet(false);
            setSelectedTemplate({ ...template, primaryMuscle: template.primary_muscle_group, isPending: true });
            setSearchQuery(template.name);
            setName(template.name);
            setShowDropdown(false);
          }}
        />
      )}
    </div>
  );
}

/* ===== SortableExercise ===== */
// PB-011: header inline della superserie nel Builder. Il "Recupero tra i giri" e'
// una proprieta' del GRUPPO (workout + lettera), non del singolo esercizio. Vuoto
// = "non impostato" -> nessun timer a fine giro (nessun default 90).
function SupersetHeader({ letter, color, rest, onCommit, onDissolve }) {
  const [val, setVal] = useState(rest == null ? '' : String(rest));
  useEffect(() => { setVal(rest == null ? '' : String(rest)); }, [rest]);
  const commit = () => {
    const trimmed = val.trim();
    if (trimmed === '') { onCommit(null); return; }
    const n = parseInt(trimmed, 10);
    onCommit(Number.isFinite(n) && n >= 0 ? n : null);
  };
  return (
    <div className="pe-ss-head">
      {/* PB-014: riga 1 — badge a sinistra, unica azione "Sciogli" a destra
          (coerente con l'header della vista di esecuzione). */}
      <div className="pe-ss-head-top">
        <span className="pe-ss-head-badge" style={{ background: color }}>SS {letter}</span>
        {onDissolve && (
          <button type="button" className="pe-ss-head-dissolve" onClick={onDissolve}>Sciogli</button>
        )}
      </div>
      <div className="pe-ss-head-rest">
        <span className="pe-ss-head-label">Recupero tra i giri</span>
        <span className="pe-ss-head-input">
          <input
            type="number"
            min="0"
            inputMode="numeric"
            placeholder="non impostato"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
          />
          <span className="pe-ss-head-unit">s</span>
        </span>
      </div>
    </div>
  );
}

function SortableExercise({ exercise, isFirst, isLast, onEdit, onDelete, onMoveUp, onMoveDown, supersetMode, isSelected, onToggleSelection, getSupersetColor, onRemoveSuperset, inSupersetGroup }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: exercise.id });
  const ssColor = exercise.superset_group ? getSupersetColor(exercise.superset_group) : null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // PB-012: dentro il contenitore della superserie il colore/bordo è dato dal
    // wrapper; il bordo sinistro resta solo per i membri "stray" fuori dal gruppo.
    ...(ssColor && !inSupersetGroup && { borderLeft: `3px solid ${ssColor}` })
  };

  return (
    <div ref={setNodeRef} style={style} className={`pe-exercise${inSupersetGroup ? ' pe-exercise--in-ss' : ''}`}>
      {supersetMode && (
        <input type="checkbox" checked={isSelected} onChange={onToggleSelection} className="pe-exercise-check" />
      )}
      {!supersetMode && (
        <div className="pe-drag" {...attributes} {...listeners}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="8" cy="4" r="2"/><circle cx="16" cy="4" r="2"/><circle cx="8" cy="12" r="2"/><circle cx="16" cy="12" r="2"/><circle cx="8" cy="20" r="2"/><circle cx="16" cy="20" r="2"/></svg>
        </div>
      )}
      <div className="pe-exercise-info">
        <div className="pe-exercise-top">
          <span className="pe-exercise-name">{exercise.name}</span>
          {/* PB-012: dentro il contenitore il badge "SS" è già nell'header del gruppo;
              lo mostriamo solo per i membri "stray" fuori dal contenitore. */}
          {exercise.superset_group && !supersetMode && !inSupersetGroup && (
            <span className="pe-ss-badge" style={{ background: ssColor }}>SS {exercise.superset_group}</span>
          )}
        </div>
        <span className="pe-exercise-detail">
          {exercise.duration_minutes ? (
            // Cardio display
            <>
              {exercise.duration_minutes} min
              {exercise.target_speed ? ` · ${exercise.target_speed} km/h` : ''}
              {exercise.target_incline ? ` · ${exercise.target_incline}%` : ''}
              {exercise.target_distance ? ` · ${exercise.target_distance} km` : ''}
            </>
          ) : (
            // Strength display
            <>
              {exercise.sets} x {exercise.reps}
              {exercise.weight ? ` @ ${exercise.weight}kg` : ''}
              {/* PB-011: nei membri di una superserie il recupero individuale e'
                  ignorato (gestito dalla superserie) → non lo mostriamo. */}
              {!exercise.superset_group && exercise.rest_seconds ? ` · ${exercise.rest_seconds}s` : ''}
            </>
          )}
        </span>
        {exercise.notes && <span className="pe-exercise-notes">{exercise.notes}</span>}
      </div>
      {!supersetMode && (
        <div className="pe-exercise-actions">
          {/* PB-014: lo scioglimento è ora l'azione "Sciogli" nell'header del
              contenitore. Qui il pulsante resta SOLO per i membri "stray"
              (lettera senza gruppo ≥2), che non hanno un header. */}
          {exercise.superset_group && !inSupersetGroup && (
            <button className="pe-btn-rm-ss" onClick={() => onRemoveSuperset(exercise.superset_group)}>Rm SS</button>
          )}
          <button className="pe-btn-move" onClick={onMoveUp} disabled={isFirst}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button className="pe-btn-move" onClick={onMoveDown} disabled={isLast}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <button className="pe-btn-edit" onClick={() => onEdit(exercise)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button className="pe-btn-del" onClick={() => onDelete(exercise.id, exercise.name)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default ProgramEditor;
